import { osnovaApi } from '../base';
import type {
  PtoEditValues,
  PtoPositionHistory,
  PtoRdChangeDoc,
  PtoRdChangeDocDetail,
  PtoRdChangePositionBody,
  PtoRdChangeReconcileResult,
  PtoRdChangeReconcileView,
  PtoRdEditsAccess,
  PtoRdEditsReason,
  PtoRenumberResult,
  PtoSheetRef,
  PtoVolumeEdit,
  PtoVolumeEditFilter,
  PtoVolumeEditLogFilter,
  PtoVolumeEditLogPage,
  PtoVolumeEditOptions,
  PtoVolumeEditOutcome,
  PtoVolumeEditResolve,
} from '../types';

// ОСНОВА.Инженер — правки РД «Инженер+» и документы изменения, согласованные
// Заказчиком (№8 плана ПТО, выпуск 4; бэкенд — `routes/pto_rd_edits.rs`).
// Право правки — роль не ниже «Инженер+» и флаг `OSNOVA_PTO_RD_EDITS_ENABLED`
// для проекта (`getPtoRdEditsAccess`). Ошибки: 400 — проверка, 403 — роль,
// 409 — флаг выключен, позиция закрыта или привязана к КСП, 409
// `stale_version` — запись изменилась, обновите экран.

const BASE = '/agents/pto';

/** Свои теги: правки, история, журнал, сверка (`PtoRdEdit`) и документы
 *  изменения (`PtoRdChangeDoc`) — без правки общего списка тегов в `base.ts`.
 *  Позиции ревизии — общий `PtoVolume`. */
const api = osnovaApi.enhanceEndpoints({ addTagTypes: ['PtoRdEdit', 'PtoRdChangeDoc'] });

/** Что меняет действие правки: позиции ревизии, правки и журнал, счётчики
 *  позиций документов изменения. */
const EDIT_TAGS = ['PtoVolume', 'PtoRdEdit', 'PtoRdChangeDoc'] as const;

/** Записей журнала на страницу. */
export const PTO_EDIT_LOG_PAGE = 100;

/** Параметры запроса без пустых значений: `?status=` сервер считает
 *  отсутствующим, но в адрес их незачем класть. */
function params(p: object): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(p)) {
    if (typeof v === 'number' || (typeof v === 'string' && v.trim())) out[k] = v;
  }
  return out;
}

/** Тело «Исправить»: значение целиком, лист и строка, причина из списка. */
export interface PtoFixBody {
  value: PtoEditValues;
  sheet_ref: PtoSheetRef;
  reason_code: string;
  comment?: string | null;
}

/** Тело «Разделить строку»: части по порядку (первая — сама строка). */
export interface PtoSplitBody {
  parts: PtoEditValues[];
  sheet_ref: PtoSheetRef;
  comment?: string | null;
}

/** Тело «Указать кратность «на 1 …»»: явное число элементов и где оно
 *  написано (лист и строка обязательны). */
export interface PtoPerUnitBody {
  count: number;
  sheet_ref: PtoSheetRef;
  comment?: string | null;
}

/** Тело «Строка не распознана программой»: строка после строки-якоря того же
 *  листа. */
export interface PtoMissingRowBody {
  value: PtoEditValues;
  sheet_ref?: PtoSheetRef | null;
  comment?: string | null;
}

/** Тело «Спорно»: лист и причина из списка — без правки, в журнал. */
export interface PtoDisputeBody {
  sheet_ref: PtoSheetRef;
  reason_code: string;
  comment?: string | null;
}

export const ptoRdEditsApi = api.injectEndpoints({
  endpoints: (b) => ({
    /** Режим функции, право правки и справочники (причины, виды документов). */
    getPtoRdEditsAccess: b.query<PtoRdEditsAccess, string>({
      query: (projectId) => `${BASE}/projects/${projectId}/rd-edits/access`,
    }),

    // ── (А) Позиции ──

    /** Что можно сделать со строкой: признаки нераспознанного, действия,
     *  привязка КСП, прочтение программы. Всегда свежие — без кэша. */
    getPtoVolumeEditOptions: b.query<PtoVolumeEditOptions, string>({
      query: (id) => `${BASE}/volumes/${id}/edit-options`,
      providesTags: ['PtoRdEdit'],
      keepUnusedDataFor: 0,
    }),
    /** История позиции: правки и записи журнала. */
    getPtoVolumeHistory: b.query<PtoPositionHistory, string>({
      query: (id) => `${BASE}/volumes/${id}/history`,
      providesTags: ['PtoRdEdit'],
      keepUnusedDataFor: 0,
    }),
    fixPtoVolume: b.mutation<PtoVolumeEditOutcome, { id: string; body: PtoFixBody }>({
      query: ({ id, body }) => ({ url: `${BASE}/volumes/${id}/fix`, method: 'POST', body }),
      invalidatesTags: [...EDIT_TAGS],
    }),
    splitPtoVolume: b.mutation<PtoVolumeEditOutcome, { id: string; body: PtoSplitBody }>({
      query: ({ id, body }) => ({ url: `${BASE}/volumes/${id}/split`, method: 'POST', body }),
      invalidatesTags: [...EDIT_TAGS],
    }),
    perUnitPtoVolume: b.mutation<PtoVolumeEditOutcome, { id: string; body: PtoPerUnitBody }>({
      query: ({ id, body }) => ({ url: `${BASE}/volumes/${id}/per-unit`, method: 'POST', body }),
      invalidatesTags: [...EDIT_TAGS],
    }),
    /** `id` — строка-якорь: новая строка встаёт после неё. Найдена в тексте
     *  листа — в `added`, нет — правка в `pending` (ждёт подтверждения). */
    addPtoMissingRow: b.mutation<PtoVolumeEditOutcome, { id: string; body: PtoMissingRowBody }>({
      query: ({ id, body }) => ({
        url: `${BASE}/volumes/${id}/missing-row`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [...EDIT_TAGS],
    }),
    disputePtoVolume: b.mutation<PtoVolumeEditOutcome, { id: string; body: PtoDisputeBody }>({
      query: ({ id, body }) => ({ url: `${BASE}/volumes/${id}/dispute`, method: 'POST', body }),
      invalidatesTags: [...EDIT_TAGS],
    }),

    // ── Правки ──

    /** Правка и части разделённой строки. */
    getPtoVolumeEdit: b.query<{ edit: PtoVolumeEdit; children: PtoVolumeEdit[] }, string>({
      query: (id) => `${BASE}/volume-edits/${id}`,
      providesTags: ['PtoRdEdit'],
    }),
    /** Подтвердить строку, не найденную в тексте листа (другой пользователь). */
    confirmPtoVolumeEdit: b.mutation<PtoVolumeEditOutcome, { id: string; comment?: string }>({
      query: ({ id, comment }) => ({
        url: `${BASE}/volume-edits/${id}/confirm`,
        method: 'POST',
        body: { comment: comment || undefined },
      }),
      invalidatesTags: [...EDIT_TAGS],
    }),
    /** Не подтверждать (автор — «отменено», другой — «не подтверждено»). */
    declinePtoVolumeEdit: b.mutation<PtoVolumeEditOutcome, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `${BASE}/volume-edits/${id}/decline`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: [...EDIT_TAGS],
    }),
    /** «Отменить» правку (А) с причиной. */
    revertPtoVolumeEdit: b.mutation<PtoVolumeEditOutcome, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `${BASE}/volume-edits/${id}/revert`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: [...EDIT_TAGS],
    }),
    /** «Отозвать» позицию по документу изменения с причиной. */
    withdrawPtoVolumeEdit: b.mutation<PtoVolumeEditOutcome, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `${BASE}/volume-edits/${id}/withdraw`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: [...EDIT_TAGS],
    }),
    /** Решение по правке: конфликт, «строка не найдена», спорная позиция (Б). */
    resolvePtoVolumeEdit: b.mutation<
      PtoVolumeEditOutcome,
      { id: string; body: PtoVolumeEditResolve }
    >({
      query: ({ id, body }) => ({
        url: `${BASE}/volume-edits/${id}/resolve`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [...EDIT_TAGS],
    }),

    // ── Реестр и журнал ──

    /** Реестр правок проекта (новые сверху). */
    listPtoVolumeEdits: b.query<
      { items: PtoVolumeEdit[] },
      { projectId: string } & PtoVolumeEditFilter
    >({
      query: ({ projectId, ...filter }) => ({
        url: `${BASE}/projects/${projectId}/volume-edits`,
        params: params(filter),
      }),
      providesTags: ['PtoRdEdit'],
    }),
    /** Журнал правок проекта: новые сверху, «ещё» — курсор `before_id`. */
    listPtoVolumeEditLog: b.infiniteQuery<
      PtoVolumeEditLogPage,
      { projectId: string } & PtoVolumeEditLogFilter,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (last) => last.next_before_id ?? undefined,
      },
      query: ({ queryArg: { projectId, ...filter }, pageParam }) => ({
        url: `${BASE}/projects/${projectId}/volume-edit-log`,
        params: params({
          ...filter,
          before_id: pageParam > 0 ? pageParam : undefined,
          limit: PTO_EDIT_LOG_PAGE,
        }),
      }),
      providesTags: ['PtoRdEdit'],
    }),
    /** Журнал в Excel (тот же отбор). */
    exportPtoVolumeEditLogXlsx: b.query<Blob, { projectId: string } & PtoVolumeEditLogFilter>({
      query: ({ projectId, ...filter }) => ({
        url: `${BASE}/projects/${projectId}/volume-edit-log/xlsx`,
        params: params(filter),
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
      keepUnusedDataFor: 0,
    }),

    // ── (Б) Документы изменения ──

    listPtoRdChangeDocs: b.query<
      { items: PtoRdChangeDoc[]; kinds: PtoRdEditsReason[] },
      { projectId: string; document_id?: string }
    >({
      query: ({ projectId, document_id }) => ({
        url: `${BASE}/projects/${projectId}/rd-change-docs`,
        params: params({ document_id }),
      }),
      providesTags: ['PtoRdChangeDoc'],
    }),
    /** Загрузка: multipart — `meta` (JSON [`PtoRdChangeDocMeta`]) и `file`. */
    createPtoRdChangeDoc: b.mutation<PtoRdChangeDocDetail, { projectId: string; form: FormData }>({
      query: ({ projectId, form }) => ({
        url: `${BASE}/projects/${projectId}/rd-change-docs`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['PtoRdChangeDoc', 'PtoRdEdit'],
    }),
    getPtoRdChangeDoc: b.query<PtoRdChangeDocDetail, string>({
      query: (id) => `${BASE}/rd-change-docs/${id}`,
      providesTags: ['PtoRdChangeDoc'],
    }),
    /** Файл документа: текущая версия или `version`. */
    downloadPtoRdChangeDocFile: b.query<Blob, { id: string; version?: number }>({
      query: ({ id, version }) => ({
        url: `${BASE}/rd-change-docs/${id}/file`,
        params: params({ version }),
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
      keepUnusedDataFor: 0,
    }),
    /** Новая версия файла: multipart — `file` и необязательный `comment`. */
    addPtoRdChangeDocVersion: b.mutation<PtoRdChangeDocDetail, { id: string; form: FormData }>({
      query: ({ id, form }) => ({
        url: `${BASE}/rd-change-docs/${id}/versions`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['PtoRdChangeDoc', 'PtoRdEdit'],
    }),
    /** «Отозвать» документ с причиной (удаления нет): его действующие позиции
     *  отзываются тоже. */
    withdrawPtoRdChangeDoc: b.mutation<PtoRdChangeDocDetail, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `${BASE}/rd-change-docs/${id}/withdraw`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: [...EDIT_TAGS],
    }),
    /** Внести материал по документу в действующую ревизию комплекта. */
    addPtoRdChangePosition: b.mutation<
      PtoVolumeEditOutcome,
      { id: string; body: PtoRdChangePositionBody }
    >({
      query: ({ id, body }) => ({
        url: `${BASE}/rd-change-docs/${id}/positions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [...EDIT_TAGS],
    }),

    // ── Сверка позиций (Б) с новой ревизией ──

    getPtoRdChangeReconcile: b.query<PtoRdChangeReconcileView, string>({
      query: (documentId) => `${BASE}/rd-documents/${documentId}/rd-change-reconcile`,
      providesTags: ['PtoRdEdit'],
    }),
    runPtoRdChangeReconcile: b.mutation<PtoRdChangeReconcileResult, string>({
      query: (documentId) => ({
        url: `${BASE}/rd-documents/${documentId}/rd-change-reconcile`,
        method: 'POST',
      }),
      invalidatesTags: [...EDIT_TAGS],
    }),

    // ── «Перенумеровать» вручную ──

    /** Номера 1…N в каждой части в порядке РД; причина обязательна, «было →
     *  стало» — в журнал правок. */
    renumberPtoRevision: b.mutation<
      { renumbered: PtoRenumberResult },
      { revisionId: string; reason: string }
    >({
      query: ({ revisionId, reason }) => ({
        url: `${BASE}/revisions/${revisionId}/renumber`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['PtoVolume', 'PtoRdRevision', 'PtoRdEdit'],
    }),
  }),
});

export const {
  useGetPtoRdEditsAccessQuery,
  useGetPtoVolumeEditOptionsQuery,
  useGetPtoVolumeHistoryQuery,
  useFixPtoVolumeMutation,
  useSplitPtoVolumeMutation,
  usePerUnitPtoVolumeMutation,
  useAddPtoMissingRowMutation,
  useDisputePtoVolumeMutation,
  useGetPtoVolumeEditQuery,
  useConfirmPtoVolumeEditMutation,
  useDeclinePtoVolumeEditMutation,
  useRevertPtoVolumeEditMutation,
  useWithdrawPtoVolumeEditMutation,
  useResolvePtoVolumeEditMutation,
  useListPtoVolumeEditsQuery,
  useListPtoVolumeEditLogInfiniteQuery,
  useLazyExportPtoVolumeEditLogXlsxQuery,
  useListPtoRdChangeDocsQuery,
  useCreatePtoRdChangeDocMutation,
  useGetPtoRdChangeDocQuery,
  useLazyDownloadPtoRdChangeDocFileQuery,
  useAddPtoRdChangeDocVersionMutation,
  useWithdrawPtoRdChangeDocMutation,
  useAddPtoRdChangePositionMutation,
  useGetPtoRdChangeReconcileQuery,
  useRunPtoRdChangeReconcileMutation,
  useRenumberPtoRevisionMutation,
} = ptoRdEditsApi;

/** Действие «позиции и правки устарели» (теги правки): экран «Позиции объёма»
 *  сбрасывает кэш, когда строку исправили в панели окна листа РД — у окна свой
 *  кэш, экран узнаёт об этом сообщением канала (`pto_volume_changed`). */
export const invalidatePtoVolumeEdits = () => ptoRdEditsApi.util.invalidateTags([...EDIT_TAGS]);
